import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';

import { setSegmentReviews } from '../gql/services/segmentReviews';
import type { RouteSegment } from '../types/surveillanceRoute';
import {
  adoptServerResults,
  applyGesture,
  mergeOverlay,
  pruneSettled,
  retainPending,
  rollbackGesture,
  type LocalReviewMap,
} from '../utils/segmentReviewOverlay';
import { usePendingSegmentReviews } from './usePendingSegmentReviews';

const EMPTY: LocalReviewMap = new Map();

/**
 * Estado optimista de las casillas de «revisado» de la tabla de tramos.
 *
 * 🔴 El detalle del recorrido NO observa la caché de Apollo (lee con
 * `readQuery` y revalida en red), así que escribirla no repinta la tabla: la
 * casilla responde desde esta capa local, que se escribe al tocar, ANTES de la
 * red. Se mezcla con lo encolado sin red y se retiene para que no parpadee.
 */
export function useSegmentReviewState(segments: RouteSegment[]): {
  overlay: ReadonlyMap<string, boolean>;
  toggle: (segmentIds: string[], reviewed: boolean) => void;
} {
  const client = useApolloClient();
  const pending = usePendingSegmentReviews();
  const [local, setLocal] = useState<LocalReviewMap>(EMPTY);
  const tokenRef = useRef(0);

  useEffect(() => {
    setLocal((prev) => retainPending(prev, pending));
  }, [pending]);

  useEffect(() => {
    setLocal((prev) => pruneSettled(prev, segments));
  }, [segments]);

  const overlay = useMemo(() => mergeOverlay(pending, local), [pending, local]);

  const toggle = useCallback(
    (segmentIds: string[], reviewed: boolean) => {
      if (segmentIds.length === 0) return;
      tokenRef.current += 1;
      const token = tokenRef.current;
      // La hora la fija el gesto: es la que viaja y la que el servidor guarda
      // como `reviewedAt`, así que sirve para saber cuándo ya lo procesó.
      const changedAt = new Date().toISOString();
      setLocal((prev) =>
        applyGesture(prev, segmentIds, reviewed, token, changedAt)
      );
      void setSegmentReviews(client, segmentIds, reviewed, undefined, {
        changedAt,
        // Si el servidor responde otro estado (había uno más reciente), manda él.
        onServerResults: (results) =>
          setLocal((prev) => adoptServerResults(prev, token, results)),
        // Solo se deshacen los lotes que fallaron, no los ya guardados.
        onFailedIds: (failed) =>
          setLocal((prev) => rollbackGesture(prev, failed, token)),
      });
    },
    [client]
  );

  return { overlay, toggle };
}
